import { useEffect, useState } from "react";
import { useInView } from "../../hooks/useInView";

interface CountUpProps {
  /** Valor final do contador. */
  end: number;
  /** Duração da animação em ms. */
  duration?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
}

/**
 * Conta de 0 até `end` quando entra na viewport, com easing suave.
 * Sob `prefers-reduced-motion` mostra o valor final direto.
 */
export function CountUp({
  end,
  duration = 1500,
  prefix = "",
  suffix = "",
  className,
}: CountUpProps) {
  const { ref, inView } = useInView<HTMLSpanElement>();
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      setValue(end);
      return;
    }

    let raf = 0;
    let start: number | null = null;
    const step = (ts: number) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setValue(Math.round(eased * end));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {value.toLocaleString("pt-BR")}
      {suffix}
    </span>
  );
}
